import React from 'react';
import { useNavigate } from 'react-router-dom';

import { Box, Grid, IconButton, Skeleton } from '@mui/material';

import styled from '@emotion/styled';
import { useSnackbar } from 'notistack';

import { venueFetchLatestShopifyProductsApi } from '../../../api/shopify/venueFetchLatestShopifyProductsApi';
import { useVenueListShopifyStoreProductsQuery } from '../../../hooks/reactQuery/shopify/useVenueListShopifyStoreProductsQuery';
import useVenueId from '../../../hooks/useVenueId';
import Icons from '../../../themes/icons';
import { extractShopifyProductId } from '../../../utils/shopify';
import Button from '../../Button/Button';
import BaseDialog, { BaseDialogProps } from '../Base';
import { ProductCard } from './components/ProductCard';

type Props = {
  title?: string;
} & BaseDialogProps;

const ProductCardSkeleton = () => (
  <Skeleton variant='rectangular' width='100%' height={200} sx={{ borderRadius: '8px' }} />
);

export const ShopifyProductsDialog: React.FC<Props> = ({ title = 'Shopify products', ...rest }) => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const venueID = useVenueId();
  const {
    data: products,
    isFetching: isProductsFetching,
    isLoading: isProductsLoading,
  } = useVenueListShopifyStoreProductsQuery({ venueID: String(venueID) || '' });

  const [selectedProductID, setSelectedProductID] = React.useState('');
  const [isProductsFetchingStarted, setIsProductsFetchingStarted] = React.useState(false);

  const onProductSelect = React.useCallback(
    (productID: string) => {
      setSelectedProductID((prev) => (prev === productID ? '' : productID));
    },
    [setSelectedProductID],
  );

  const handleFetchShopifyProducts = async () => {
    try {
      if (venueID) {
        setIsProductsFetchingStarted(true);
        await venueFetchLatestShopifyProductsApi({ venueID: String(venueID) || '' });
        enqueueSnackbar(
          'Products fetching started in the background please check it again shortly',
          { variant: 'success' },
        );
        setIsProductsFetchingStarted(false);
      }
    } catch (error) {
      setIsProductsFetchingStarted(false);
      console.error(error);
      enqueueSnackbar('Something want wrong while refresh the products', { variant: 'error' });
    }
  };

  const handleCreateProductOffer = React.useCallback(() => {
    // Handle the creation of a product offer here
    navigate(`/shopify-products/create/${extractShopifyProductId(selectedProductID)}`);
  }, [selectedProductID]);

  return (
    <BaseDialog
      width='800px'
      height={'750px'}
      titleComponent={
        <Box display={'flex'} alignItems={'center'} gap={'8px'}>
          <ModalTitle>{title}</ModalTitle>
          <IconButton disabled={isProductsFetchingStarted} onClick={handleFetchShopifyProducts}>
            <Icons.RefreshIcon />
          </IconButton>
        </Box>
      }
      {...rest}
    >
      <Grid container spacing={2}>
        {(isProductsFetching || isProductsLoading) && (
          <>
            <Grid item xs={6}>
              <ProductCardSkeleton />
            </Grid>
            <Grid item xs={6}>
              <ProductCardSkeleton />
            </Grid>
            <Grid item xs={6}>
              <ProductCardSkeleton />
            </Grid>
          </>
        )}
        {!isProductsFetching &&
          !isProductsLoading &&
          products?.map((product) => (
            <Grid item xs={6}>
              <ProductCard
                product={product}
                selected={selectedProductID === product.productID}
                onSelection={() => {
                  onProductSelect(product.productID);
                }}
              />
            </Grid>
          ))}
      </Grid>

      {selectedProductID && (
        <Button
          sx={{ position: 'absolute', bottom: '20px', right: '20px' }}
          variant='contained'
          color='primary'
          onClick={handleCreateProductOffer}
        >
          Create a product offer
        </Button>
      )}
    </BaseDialog>
  );
};

const ModalTitle = styled.span`
  color: #848484;
  font-size: 15px;
  font-weight: 500;
`;
